import React, { Component } from 'react';
import { connect } from 'react-redux';
import CommonHeader from '../../util_components/common_header';
import CustomInput from '../../util_components/custom_input';
import CustomSingleSelect from '../../util_components/custom_single_select';
import Button from '../../util_components/button';
import CmsImageFields from '../sales_team/CmsImageFields';
import {
  FIRST_NAME_REQUIRED,
  first_name_missing,
  full_name,
  name_parts
} from '../sales_team/cms_user_fields';
import { get_api_url } from '../../../utils/urls';
import { api_with_method, get_api } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';

const PROFILE_PHONE_CODES = [
  { label: 'India', value: '91' },
  { label: 'USA', value: '1' }
];

const DEFAULT_PHONE_CC = '91';

const split_phone_number = (phone_number) => {
  if (!phone_number) {
    return { phone_cc: DEFAULT_PHONE_CC, phone_no: '' };
  }
  // Storage format is "+<cc><no>" — strip the leading '+' before matching, but
  // tolerate legacy rows that were saved without it.
  const digits = phone_number.startsWith('+') ? phone_number.slice(1) : phone_number;
  // Longest-prefix match across the supported codes so multi-digit codes win
  // over their shorter overlaps.
  const sorted_codes = [...PROFILE_PHONE_CODES].sort((a, b) => b.value.length - a.value.length);
  const match = sorted_codes.find((c) => digits.startsWith(c.value));
  if (match) {
    return { phone_cc: match.value, phone_no: digits.slice(match.value.length) };
  }
  return { phone_cc: DEFAULT_PHONE_CC, phone_no: digits };
};

class Profile extends Component {
  state = {
    loading: true,
    saving: false,
    uploading: false,
    first_name: '',
    last_name: '',
    profile_photo: '',
    signature_image: '',
    email: '',
    phone_cc: DEFAULT_PHONE_CC,
    phone_no: ''
  };

  componentDidMount() {
    this.load_profile();
  }

  load_profile = () => {
    const url = get_api_url('/v2/cms_user/user_profile', false, true);
    get_api(url, true)
      .then((res) => {
        const data = res.data?.data || {};
        const { phone_cc, phone_no } = split_phone_number(data.phone_number || '');
        this.setState({
          loading: false,
          ...name_parts(data),
          profile_photo: data.profile_photo || '',
          signature_image: data.signature_image || '',
          email: data.email || '',
          phone_cc,
          phone_no
        });
      })
      .catch((err) => {
        this.setState({ loading: false });
        const message = err?.response?.data?.reason || 'Failed to load profile';
        this.props.set_notification_variable(true, 'error', message);
      });
  };

  handle_change = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  save_profile = () => {
    const { first_name, last_name, profile_photo, signature_image, phone_cc, phone_no } =
      this.state;
    if (this.state.uploading) return;
    if (first_name_missing(first_name)) {
      this.props.set_notification_variable(true, 'error', FIRST_NAME_REQUIRED);
      return;
    }
    const payload = {
      name: full_name(first_name, last_name),
      first_name: first_name.trim(),
      last_name: last_name.trim(),
      profile_photo,
      signature_image,
      phone_cc,
      phone_no: phone_no.trim()
    };
    this.setState({ saving: true });
    const url = get_api_url('/v2/cms_user/user_profile', false, true);
    api_with_method('patch', url, payload, true)
      .then((res) => {
        const data = res.data?.data || {};
        const next = split_phone_number(data.phone_number || '');
        const parts = data.name || data.first_name ? name_parts(data) : { first_name, last_name };
        this.setState({
          saving: false,
          ...parts,
          phone_cc: next.phone_cc,
          phone_no: next.phone_no
        });
        this.props.set_notification_variable(true, 'success', 'Profile updated');
        this.props.get_permissions();
      })
      .catch((err) => {
        this.setState({ saving: false });
        const message = err?.response?.data?.reason || 'Failed to update profile';
        this.props.set_notification_variable(true, 'error', message);
      });
  };

  render() {
    const {
      loading,
      saving,
      first_name,
      last_name,
      profile_photo,
      signature_image,
      email,
      phone_cc,
      phone_no
    } = this.state;
    return (
      <CommonHeader loading={loading} title="My Profile">
        <div style={{ maxWidth: '480px', padding: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Email</div>
            <div>{email || '--'}</div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <CustomInput
              label="First Name *"
              value={first_name}
              name="first_name"
              onChange={this.handle_change}
            />
            {first_name_missing(first_name) && !loading ? (
              <div className="text-xs text-state-error" data-testid="first-name-required">
                {FIRST_NAME_REQUIRED}
              </div>
            ) : null}
          </div>
          <div style={{ marginBottom: '16px' }}>
            <CustomInput
              label="Last Name"
              value={last_name}
              name="last_name"
              onChange={this.handle_change}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <CmsImageFields
              profile_photo={profile_photo}
              signature_image={signature_image}
              on_change={(key, url) => this.setState({ [key]: url })}
              notify={(type, message) => this.props.set_notification_variable(true, type, message)}
              on_uploading={(uploading) => this.setState({ uploading })}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <CustomSingleSelect
              label="Phone Country"
              options={PROFILE_PHONE_CODES}
              onChange={this.handle_change}
              value={phone_cc}
              name="phone_cc"
            />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <CustomInput
              label="Phone Number"
              value={phone_no}
              name="phone_no"
              onChange={this.handle_change}
            />
          </div>
          <Button
            onClick={this.save_profile}
            disabled={saving || this.state.uploading || first_name_missing(first_name)}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>
      </CommonHeader>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, message) =>
    dispatch(actions.set_notification_variable(show, type, message)),
  get_permissions: () => dispatch(actions.get_permissions())
});

export default connect(null, mapDispatchToProps)(Profile);
